// This file has been copied and ported from AstInterpreter.cs. It must be kept identical as the original file.
import { AstExpression, Token, AstPrimitive } from '../models/be/db/FormSpecification';
import { getNestedFieldValue, single, getLocaleDate, isUniversalDateString } from '../lib/utils';
import { hasOthernamesFieldname } from './constants';

/// <summary>
/// This is an Intepreter that evaulates Abstract Syntax Tree (https://en.wikipedia.org/wiki/Abstract_syntax_tree)
/// expression with respect to a given object.
///
/// Each AST expression is made up of tokens. Each token can be another expression, a keyword, a symbol or a primitive.
/// Primitives types are: bool, string, DateTime, int; all of which can be null.
/// Supported literal values are: null, strings (which must begin and end with quotes within the string) and booleans.
/// </summary>
export const equals = 'equals';
export const notEquals = 'notEquals';
export const and = 'and';
export const or = 'or';
export const not = 'not';
export const lessThan = 'lessThan';
export const If = 'if';
export const now = 'now';
export const addYears = 'addYears';

const keywords = [equals, notEquals, and, or, not, lessThan, If, now, addYears];

// if
const affirmativeBodyIndex = 1;
const negativeBodyIndex = 2;

// addYears
const dateOperandIndex = 0;
const yearsOperandIndex = 1;

export const evaluate = (expression: AstExpression, context: unknown) => {
	validateExpression(expression, context);
	const firstToken = expression[0] as string;
	const operandTokens = expression.slice(1).map((t) => evaluateOperand(t, context));
	switch (firstToken) {
		case equals:
			return operandTokens[0] === operandTokens[1];
		case notEquals:
			return operandTokens[0] !== operandTokens[1];
		case and:
			return operandTokens.map(convertOperandToBool).reduce((current, next) => current && next);
		case or:
			return operandTokens.map(convertOperandToBool).reduce((current, next) => current || next);
		case not:
			return !convertOperandToBool(single(operandTokens) as boolean);
		case lessThan:
			return evaluateLessThan(operandTokens[0] as string, operandTokens[1] as string);
		case If:
			// This evaulates both bodies and only returns one. This may cause a bug in the future.
			return convertOperandToBool(operandTokens[0] as boolean)
				? operandTokens[affirmativeBodyIndex]
				: operandTokens[negativeBodyIndex];
		case now:
			// Get today's date without any time.
			return new Date(new Date().toDateString());
		case addYears:
			return addYearsToDate(parseDate(operandTokens[dateOperandIndex]), operandTokens[yearsOperandIndex] as number);
		default:
			throw new ValidationException(`Unknown keyword: ${firstToken}.`);
	}
};

export const validateExpression = (expression: AstExpression, context: unknown) => {
	const firstToken = expression[0];
	if (firstToken === null) {
		throw new ValidationException('First token can not be null.');
	}
	if (typeof firstToken === 'string') {
		const tokenString = firstToken as string;
		if (isNullOrWhiteSpace(tokenString)) {
			throw new ValidationException(`Invalid token: "${tokenString}".`);
		}
		if (isStringLiteral(tokenString)) {
			throw new ValidationException(`First token can not be a string literal: ${tokenString}.`);
		} else if (isKeywordOrSymbol(tokenString)) {
			switch (firstToken) {
				case equals:
					validateFunction(expression, context, 2, 2);
					return 'boolean';
				case notEquals:
					validateFunction(expression, context, 2, 2);
					return 'boolean';
				case and:
					validateFunction(expression, context, 2, null, true, 'boolean');
					return 'boolean';
				case or:
					validateFunction(expression, context, 2, null, true, 'boolean');
					return 'boolean';
				case not:
					validateFunction(expression, context, 1, 1, true, 'boolean');
					return 'boolean';
				case lessThan:
					validateFunction(expression, context, 2, 2, true, dateTimeType);
					return 'boolean';
				case If: {
					const operandTypes = validateFunction(expression, context, 3, 3, false);
					const conditionIndex = 0;
					validateOperandType(operandTypes[conditionIndex], 'boolean', 'If condition');
					if (
						operandTypes[affirmativeBodyIndex] !== null &&
						operandTypes[negativeBodyIndex] !== null &&
						operandTypes[affirmativeBodyIndex] !== operandTypes[negativeBodyIndex]
					) {
						throw new ValidationException(
							`If expression's body don't have matching types: ${stringifyExpression(expression)}.`
						);
					}
					return operandTypes[affirmativeBodyIndex];
				}
				case now:
					validateFunction(expression, context, null, 0);
					return dateTimeType;
				case addYears: {
					const operandTypes = validateFunction(expression, context, 2, 2, false);
					validateDateOperandType(operandTypes[dateOperandIndex], dateTimeType);
					validateOperandType(operandTypes[yearsOperandIndex], 'number', 'year');
					return dateTimeType;
				}
				default:
					throw new ValidationException(`Unknown keyword: ${firstToken}.`);
			}
		} else {
			throw new ValidationException(`Poorly formed token: ${tokenString}.`);
		}
	} else {
		throw new ValidationException('The first token of an expression is not a string.');
	}
};

const evaluateOperand = (token: Token, context: unknown): AstPrimitive => {
	if (token === null) {
		return null;
	}
	if (isAstExpression(token)) {
		return evaluate(token as AstExpression, context);
	}
	if (typeof token === 'string') {
		const tokenString = token as string;
		if (isNullOrWhiteSpace(tokenString)) {
			throw new ValidationException(`Invalid token: "${tokenString}".`);
		} else if (isStringLiteral(tokenString)) {
			const stringQuoteSize = 1;
			// Note that this is different from the C# code as slice works differently to C#'s Substring.
			return tokenString.slice(stringQuoteSize, tokenString.length - stringQuoteSize);
		} else if (isKeywordOrSymbol(tokenString)) {
			const value = getNestedFieldValue(context, tokenString);

			// Additional names
			if (value === undefined && tokenString === hasOthernamesFieldname) {
				const additionalNames = getNestedFieldValue(context, 'additionalNames');
				return additionalNames !== undefined && additionalNames.length > 0;
			}

			if (value !== undefined) {
				// This code has a bug in it that if a string field has a date value, it will be treated like a DateTime.
				if (typeof value === 'string' && isUniversalDateString(value)) {
					return new Date(value);
				}
				return value;
			}
			throw new ValidationException(`Symbol does not exist in context: ${tokenString}.`);
		} else {
			throw new ValidationException(`Poorly formed token: ${token}.`);
		}
	} else if (typeof token === 'boolean') {
		return token;
	} else if (token instanceof Date) {
		return token;
	} else if (typeof token === 'number') {
		return token;
	} else {
		throw new ValidationException(`Unknown type for token: ${token}.`);
	}
};

const convertOperandToBool = (operandToken: boolean): boolean => {
	return operandToken === null ? false : operandToken;
};

const evaluateLessThan = (firstOperandToken: string, secondOperandToken: string) => {
	if (firstOperandToken == null || secondOperandToken == null) {
		return false; // If either operand is null, then we just return false. This is weak typing.
	}
	const firstDate = new Date(firstOperandToken);
	const secondDate = new Date(secondOperandToken);
	if (Number.isNaN(firstDate) || Number.isNaN(secondDate)) {
		throw new ValidationException('lessThan operands must be dates.');
	}
	return firstDate < secondDate;
};

const validateNonKeywordToken = (token: Token, context: unknown) => {
	if (token === null) {
		return null;
	}
	if (isAstExpression(token)) {
		return validateExpression(token as AstExpression, context);
	}
	if (typeof token === 'string') {
		const tokenString = token as string;
		if (isNullOrWhiteSpace(tokenString)) {
			throw new ValidationException(`Invalid token: "${tokenString}".`);
		} else if (isStringLiteral(tokenString)) {
			return 'string';
		} else if (isKeywordOrSymbol(tokenString)) {
			switch (tokenString) {
				case equals:
				case notEquals:
				case and:
				case or:
				case not:
				case lessThan:
				case If:
				case now:
					throw new ValidationException(`This token can not be a keyword: ${tokenString}.`);
				default: {
					const value = getNestedFieldValue(context, tokenString);
					if (value !== undefined) {
						// This code has a bug in it that if a string field has a date value, it will be treated like a DateTime.
						if (typeof value === 'string' && isUniversalDateString(value)) {
							return dateTimeType;
						}
						return value === null ? null : typeof value;
					}
					if (tokenString.startsWith(customFieldsPrefix)) {
						return null;
					}
					throw new ValidationException(`Symbol does not exist: ${tokenString}.`);
				}
			}
		} else {
			throw new ValidationException(`Poorly formed token: ${token}.`);
		}
	} else if (typeof token === 'boolean') {
		return typeof token;
	} else if (token instanceof Date) {
		return dateTimeType;
	} else if (typeof token === 'number') {
		return 'number';
	} else {
		throw new ValidationException(`Unknown type for token: ${token}.`);
	}
};

const isStringLiteral = (tokenString: string) => {
	return tokenString[0] === '"' && tokenString[tokenString.length - 1] === '"';
};

const isKeywordOrSymbol = (tokenString: string) => {
	return tokenString[0] !== '"' && tokenString[tokenString.length - 1] !== '"';
};

const validateFunction = (
	expression: AstExpression,
	context: unknown,
	minimumNumberOfOperands: number = null,
	maximumNumberOfOperands: number = null,
	validateOperandTypes = true,
	operandType: string = null
) => {
	const numberOfParameters = expression.length - 1; // Subtract one for the function keyword.
	if (minimumNumberOfOperands !== null && numberOfParameters < minimumNumberOfOperands) {
		throw new ValidationException(
			`Expression has less than the minimum number of operands: ${stringifyExpression(expression)}.`
		);
	}

	if (maximumNumberOfOperands !== null && numberOfParameters > maximumNumberOfOperands) {
		throw new ValidationException(`Expression has more than the maxinum number of operands: ${expression}.`);
	}

	const operandTypes: string[] = [];
	// Skip the function keyword.
	// eslint-disable-next-line no-restricted-syntax
	for (const operand of expression.slice(1)) {
		const currentOperandType = validateNonKeywordToken(operand, context);
		if (
			validateOperandTypes &&
			operandType !== null &&
			currentOperandType !== null &&
			operandType !== currentOperandType
		) {
			throw new ValidationException(`Operand has an invalid type: ${currentOperandType}.`);
		}
		operandTypes.push(currentOperandType);
		// eslint-disable-next-line no-param-reassign
		operandType = currentOperandType ?? operandType;
	}
	return operandTypes;
};

const stringifyExpression = (expression: AstExpression): string => {
	const strings = expression.map((x) => {
		if (isAstExpression(x)) {
			// eslint-disable-next-line @typescript-eslint/ban-types
			return `[${stringifyExpression(x as AstExpression)}]`;
		}
		return `"${x.toString()}"`;
	});
	return strings.join(', ');
};

const validateOperandType = (operandType: string, expectedType: string, operandName: string) => {
	if (operandType !== expectedType) {
		throw new ValidationException(
			`operand ${operandName} is not type ${expectedType} but instead is type ${operandType}.`
		);
	}
};

const validateDateOperandType = (operandType: string, operandName: string) => {
	if (operandType !== dateTimeType && operandType !== 'string') {
		throw new ValidationException(
			`operand ${operandName} is neither a DateTime or a string but instead is type ${operandType}.`
		);
	}
};

const parseDate = (value: Token) => {
	if (value instanceof Date) {
		return value;
	}
	if (typeof value === 'string') {
		return getLocaleDate(value);
	}
	throw new ValidationException(`Unknown type: ${typeof value}.`);
};

// Extra Javascript code that was needed by the ported code.

const dateTimeType = 'datetime';
export const customFieldsPrefix = 'customFields';

const isNullOrWhiteSpace = (value: string) => {
	return value === null || value === '' || /^\s+$/.test(value);
};

export const isAstExpression = (value: unknown) => {
	// If a non-AST array ever contains a keyword, this will return the wrong value.
	return Array.isArray(value) && value.length > 0 && keywords.indexOf(value[0]) !== -1;
};

export class ValidationException extends Error {}

const addYearsToDate = (date: Date, years: number) => {
	const newDate = new Date(date.getTime());
	newDate.setFullYear(newDate.getFullYear() + years);
	return newDate;
};
